<template>
  <div class="metric-grid-card" :class="cardClasses" @click="handleClick">
    <!-- Header -->
    <div class="card-header">
      <h3 class="card-title">{{ config.title }}</h3>
      <span v-if="config.badge" :class="badgeClasses">{{ config.badge }}</span>
    </div>

    <!-- Grid Content -->
    <div class="metric-grid" :class="gridClasses">
      <div
        v-for="(metric, index) in config.metrics"
        :key="`metric-${index}`"
        class="metric-item"
        :class="getMetricItemClasses()"
        @click.stop="handleMetricClick(metric, index)"
      >
        <!-- Icon -->
        <div
          v-if="metric.icon"
          class="metric-icon"
          :class="getIconClasses(metric.color)"
        >
          <i class="material-icons">{{ metric.icon }}</i>
        </div>

        <!-- Content -->
        <div class="metric-content">
          <div class="metric-value" :class="getValueClasses(metric.color)">
            {{ formatValue(data[metric.key]) }}
          </div>
          <div class="metric-label">{{ metric.label }}</div>
        </div>

        <!-- Trend/Status Indicator -->
        <div
          v-if="metric.showTrend && getTrendData(metric.key)"
          class="metric-trend"
        >
          <i
            class="material-icons trend-icon"
            :class="getTrendClasses(metric.key)"
          >
            {{ getTrendIcon(metric.key) }}
          </i>
        </div>

        <!-- Action Button -->
        <div v-if="metric.action" class="metric-action">
          <button class="action-btn" :class="getActionClasses(metric.color)">
            <i class="material-icons">{{
              metric.action.icon || "arrow_forward"
            }}</i>
          </button>
        </div>
      </div>
    </div>

    <!-- Summary Footer -->
    <div v-if="showSummary" class="card-footer">
      <div class="summary-stats">
        <span class="summary-label">Total Items:</span>
        <span class="summary-value">{{ totalItems }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MetricGridCard",
  props: {
    config: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    cardClasses() {
      return [
        "bg-white",
        "rounded-xl",
        "shadow-sm",
        "border",
        "border-gray-100",
        "p-6",
        "hover:shadow-md",
        "transition-all",
        "duration-300",
        "cursor-pointer",
        "hover:border-gray-200"
      ];
    },

    badgeClasses() {
      const colorMap = {
        green: "bg-emerald-100 text-emerald-700",
        blue: "bg-blue-100 text-blue-700",
        red: "bg-red-100 text-red-700",
        yellow: "bg-yellow-100 text-yellow-700",
        purple: "bg-purple-100 text-purple-700"
      };

      return [
        "inline-flex",
        "items-center",
        "px-2.5",
        "py-0.5",
        "rounded-full",
        "text-xs",
        "font-medium",
        colorMap[this.config.badgeColor] || colorMap.blue
      ];
    },

    gridClasses() {
      const layoutMap = {
        "grid-2x2": "grid-cols-2 gap-4",
        "grid-2x3": "grid-cols-2 gap-4",
        "grid-3x2": "grid-cols-3 gap-3",
        "grid-1x4": "grid-cols-1 gap-3",
        "grid-4x1": "grid-cols-4 gap-2"
      };

      const responsiveMap = {
        "grid-2x2": "sm:grid-cols-2",
        "grid-2x3": "sm:grid-cols-2",
        "grid-3x2": "sm:grid-cols-3 md:grid-cols-3",
        "grid-1x4": "sm:grid-cols-1",
        "grid-4x1": "sm:grid-cols-2 md:grid-cols-4"
      };

      const layout = this.config.layout || "grid-2x2";

      return [
        "grid",
        "grid-cols-1", // Mobile default
        responsiveMap[layout],
        layoutMap[layout] || layoutMap["grid-2x2"]
      ];
    },

    totalItems() {
      if (!this.config.metrics) return 0;
      return this.config.metrics.reduce((sum, metric) => {
        return sum + (this.data[metric.key] || 0);
      }, 0);
    },

    showSummary() {
      return this.config.showSummary === true;
    }
  },
  methods: {
    getMetricItemClasses() {
      return [
        "metric-item-base",
        "p-4",
        "rounded-lg",
        "border",
        "border-gray-100",
        "hover:border-gray-200",
        "hover:shadow-sm",
        "transition-all",
        "duration-200",
        "cursor-pointer",
        "group"
      ];
    },

    getIconClasses(color) {
      const colorMap = {
        blue: "bg-blue-100 text-blue-600",
        green: "bg-emerald-100 text-emerald-600",
        red: "bg-red-100 text-red-600",
        yellow: "bg-yellow-100 text-yellow-600",
        purple: "bg-purple-100 text-purple-600",
        amber: "bg-amber-100 text-amber-600",
        indigo: "bg-indigo-100 text-indigo-600",
        emerald: "bg-emerald-100 text-emerald-600"
      };

      return [
        "w-10",
        "h-10",
        "rounded-lg",
        "flex",
        "items-center",
        "justify-center",
        "mb-3",
        "group-hover:scale-110",
        "transition-transform",
        "duration-200",
        colorMap[color] || colorMap.blue
      ];
    },

    getValueClasses(color) {
      const colorMap = {
        blue: "text-blue-600",
        green: "text-emerald-600",
        red: "text-red-600",
        yellow: "text-yellow-600",
        purple: "text-purple-600",
        amber: "text-amber-600",
        indigo: "text-indigo-600",
        emerald: "text-emerald-600"
      };

      return [
        "text-2xl",
        "font-bold",
        "leading-none",
        "mb-1",
        colorMap[color] || "text-gray-900"
      ];
    },

    getActionClasses(color) {
      const colorMap = {
        blue: "bg-blue-50 text-blue-600 hover:bg-blue-100",
        green: "bg-emerald-50 text-emerald-600 hover:bg-emerald-100",
        red: "bg-red-50 text-red-600 hover:bg-red-100",
        yellow: "bg-yellow-50 text-yellow-600 hover:bg-yellow-100",
        purple: "bg-purple-50 text-purple-600 hover:bg-purple-100",
        amber: "bg-amber-50 text-amber-600 hover:bg-amber-100",
        indigo: "bg-indigo-50 text-indigo-600 hover:bg-indigo-100",
        emerald: "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
      };

      return [
        "w-8",
        "h-8",
        "rounded-full",
        "flex",
        "items-center",
        "justify-center",
        "transition-all",
        "duration-200",
        "opacity-0",
        "group-hover:opacity-100",
        colorMap[color] || colorMap.blue
      ];
    },

    formatValue(value) {
      if (typeof value === "number") {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(1) + "M";
        }
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + "K";
        }
        return value.toLocaleString();
      }
      return value || "0";
    },

    getTrendData(key) {
      return this.data.trends && this.data.trends[key];
    },

    getTrendIcon(key) {
      const trend = this.getTrendData(key);
      if (!trend) return "";
      return trend > 0
        ? "trending_up"
        : trend < 0
        ? "trending_down"
        : "trending_flat";
    },

    getTrendClasses(key) {
      const trend = this.getTrendData(key);
      if (!trend) return "";
      return trend > 0
        ? "text-green-500"
        : trend < 0
        ? "text-red-500"
        : "text-gray-400";
    },

    handleClick() {
      this.$emit("card-click", {
        type: "metric-grid",
        config: this.config,
        data: this.data
      });
    },

    handleMetricClick(metric, index) {
      this.$emit("metric-click", {
        metric,
        index,
        value: this.data[metric.key],
        config: this.config
      });
    }
  }
};
</script>

<style scoped>
.metric-grid-card {
  position: relative;
  overflow: hidden;
}

.metric-grid-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.metric-grid-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.metric-grid {
  display: grid;
  width: 100%;
}

.metric-item-base {
  position: relative;
  background: #fafafa;
  border: 1px solid #f0f0f0;
}

.metric-item-base:hover {
  background: #f8fafc;
  border-color: #e2e8f0;
  transform: translateY(-1px);
}

.metric-content {
  text-align: center;
}

.metric-value {
  font-variant-numeric: tabular-nums;
}

.metric-label {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.2;
  font-weight: 500;
}

.metric-trend {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}

.trend-icon {
  font-size: 1rem;
}

.metric-action {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}

.action-btn {
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.card-footer {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.summary-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.summary-value {
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 600;
}

/* Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-grid-card {
  animation: slideInUp 0.4s ease-out;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .metric-grid {
    gap: 0.75rem !important;
  }

  .metric-item-base {
    padding: 1rem;
  }

  .metric-label {
    font-size: 0.75rem;
  }
}
</style>
